import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Ban, Gavel, IdCard, LogOut, ShieldCheck, Users } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchUsers, banUser, unbanUser, forceLogout, type AdminUserRow } from '@/lib/adapters'
import { ApiError } from '@/lib/api'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, PIIField, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'

/**
 * A3.01 — Foydalanuvchilar ro'yxati + A3.27/A3.28 bloklash/chiqarish.
 *
 * Bloklash endi X2 DARAJA 2: `POST /admin/users/:id/ban {reason}` sababni
 * qabul qiladi va auditga yozadi (backend `f656742`). DangerDialog L2 sababni
 * majburiy qiladi; blokdan chiqarish — to'g'ridan-to'g'ri (kirishni tiklaydi).
 */
export function UsersScreen() {
  const t = useT()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [banTarget, setBanTarget] = useState<AdminUserRow | null>(null)
  const [logoutTarget, setLogoutTarget] = useState<AdminUserRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const nameOf = (r: AdminUserRow) =>
    [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.username ? `@${r.username}` : `#${r.id}`)

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(t.users.actionDone, { tone: 'success' })
        setReloadKey((k) => k + 1)
      } catch (e) {
        toast(t.users.actionFailed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
        throw e
      }
    },
    [toast, t],
  )

  const rowActions = useMemo<RowAction<AdminUserRow>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/users/${r.id}`),
      },
      {
        id: 'ban',
        label: t.users.ban,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        visible: (r) => r.status !== 'banned',
        onSelect: (r) => setBanTarget(r),
      },
      {
        id: 'unban',
        label: t.users.unban,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.USERS_BAN,
        visible: (r) => r.status === 'banned',
        onSelect: (r) => void run(() => unbanUser(r.id)),
      },
      {
        id: 'force-logout',
        label: t.users.forceLogout,
        icon: <LogOut size={13} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: (r) => setLogoutTarget(r),
      },
    ],
    [t, run, navigate],
  )

  const columns = useMemo<AdminColumn<AdminUserRow>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 90 },
      {
        key: 'name',
        header: t.col.name,
        type: 'text',
        accessor: (r) =>
          [r.first_name, r.last_name].filter(Boolean).join(' ') || (r.username ? `@${r.username}` : ''),
        sortable: false,
        minWidth: 160,
      },
      { key: 'username', header: t.col.username, type: 'text', width: 150 },
      {
        key: 'phone',
        header: t.col.phone,
        type: 'pii',
        piiLevel: 'L2',
        width: 160,
        sortable: false,
        filterable: false,
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'active', label: t.status.active, status: 'active' },
          { value: 'banned', label: t.status.banned, status: 'banned' },
          { value: 'deleted', label: t.status.deleted, status: 'deleted' },
        ],
      },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 140 },
    ],
    [t],
  )

  // `useCallback` — DataTable `fetcher` ni bog'liqlik sifatida kuzatadi;
  // barqaror bo'lmasa har renderda qayta so'rov ketadi.
  const fetcher = useCallback(fetchUsers, [])

  return (
    <>
      <PageHeader
        title={t.nav.users}
        screenId="A3.01"
        verified
        actions={
          <Link
            to="/users/ban-log"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            <Gavel size={13} />
            {t.users.banLog}
          </Link>
        }
      />
      <DataTable<AdminUserRow>
        key={reloadKey}
        screenId="A3.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        selectable
        exportable
        rowActions={rowActions}
        emptyState={{ icon: <Users size={28} strokeWidth={1.5} /> }}
        detailPanel={(r) => (
          <div>
            <InfoRow label={t.col.id}>
              <MonoValue value={String(r.id)} />
            </InfoRow>
            <InfoRow label={t.col.name}>
              {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
            </InfoRow>
            <InfoRow label={t.col.username}>{r.username ? `@${r.username}` : '—'}</InfoRow>
            <InfoRow label={t.col.phone}>
              {r.phone ? <PIIField value={r.phone} level="L2" /> : '—'}
            </InfoRow>
            <InfoRow label={t.col.status}>
              <StatusBadge status={r.status === 'banned' ? 'banned' : 'active'} />
            </InfoRow>
            <InfoRow label={t.col.registered}>
              <RelativeTime value={r.created_at} />
            </InfoRow>
          </div>
        )}
      />

      {banTarget && (
        <DangerDialog
          open
          onClose={() => setBanTarget(null)}
          screenId="A3.27"
          level={2}
          title={t.users.banTitle(nameOf(banTarget))}
          description={t.users.banDesc}
          confirmLabel={t.users.ban}
          onConfirm={async ({ reason }) => {
            try {
              await banUser(banTarget.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e // dialog ochiq qolsin
            }
          }}
        />
      )}

      {logoutTarget && (
        <DangerDialog
          open
          onClose={() => setLogoutTarget(null)}
          screenId="A3.31"
          level={2}
          title={t.users.forceLogoutTitle(nameOf(logoutTarget))}
          description={t.users.forceLogoutDesc}
          confirmLabel={t.users.forceLogout}
          onConfirm={async ({ reason }) => {
            try {
              await forceLogout(logoutTarget.id, reason)
              toast(t.users.actionDone, { tone: 'success' })
              setReloadKey((k) => k + 1)
            } catch (e) {
              // Endpoint (lane-A patch) hali deploy bo'lmagan bo'lsa 404 → aniq
              // "kutilmoqda" xabari (buzuq-tugma emas); dialog yopiladi.
              if (e instanceof ApiError && e.status === 404) {
                toast(t.users.forceLogoutPending, { tone: 'warning' })
                return
              }
              toast(t.users.actionFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
